import React, { useCallback, useEffect, useState } from "react";
import { DateTime } from "luxon";
import Header from "../Header/Hearder";
import axiosInstance from "../../lib/axiosInstance";
import "./RevenueReports.css";

// Backend expects day-based from/to (YYYY-MM-DD) even when UI uses input type="month" (YYYY-MM).
function getMonthFromTo(monthYear, tz) {
  const safeMy = String(monthYear || "").trim();
  const [yy, mm] = safeMy.split("-");
  const year = Number(yy);
  const month = Number(mm);

  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    const fallbackNow = DateTime.now().setZone(tz);
    return {
      from: fallbackNow.startOf("month").toISODate(),
      to: fallbackNow.endOf("month").toISODate(),
    };
  }

  const base = DateTime.fromObject({ year, month, day: 1 }, { zone: tz }).startOf("month");
  return {
    from: base.toISODate(),
    to: base.endOf("month").toISODate(),
  };
}

function buildDateRange(reportMode, reportDate, monthYear, rangeFrom, rangeTo, tz) {
  if (reportMode === "Monthly") return getMonthFromTo(monthYear, tz);
  if (reportMode === "Custom") {
    const f = rangeFrom || DateTime.now().setZone(tz).toISODate();
    const t = rangeTo || f;
    return { from: f, to: t > f ? t : f };
  }
  return { from: reportDate, to: reportDate };
}

function normalizeBillRows(payload) {
  const raw =
    (payload && Array.isArray(payload.items) && payload.items) ||
    (payload && Array.isArray(payload.data) && payload.data) ||
    (payload && Array.isArray(payload.rows) && payload.rows) ||
    (Array.isArray(payload) && payload) ||
    [];

  return raw.map((b, idx) => {
    const patientName = b?.patientName || "-";
    const patientPhone = b?.patientPhone || "";
    const invoiceUrl = b?.invoiceDownloadUrl || b?.invoiceUrl || b?.invoiceUrlFallback || "";
    return {
      id: b?._id || b?.id || `${b?.billNumber || "bill"}-${idx}`,
      date: b?.revenueDate || b?.paidAt || b?.billDate || b?.createdAt || "",
      billNumber: b?.billNumber || "-",
      patientId: b?.patientId || "",
      patientName,
      patientPhone,
      totalAmount: Number(b?.totalAmount || b?.amount || 0),
      status: String(b?.status || "paid").toLowerCase(),
      invoiceDownloaded: !!invoiceUrl,
      invoiceDownloads: 0,
      invoiceDownloadUrl: invoiceUrl,
      invoiceFileName: b?.invoiceFileName || "",
      invoiceS3Key: b?.invoiceS3Key || "",
    };
  });
}

export default function RevenueReports({ branches = [], selectedBranchId = "", onBranchChange }) {
  const tz = process.env.REACT_APP_CLINIC_TZ || "Asia/Kolkata";
  const now = DateTime.now().setZone(tz);
  const [reportDate, setReportDate] = useState(DateTime.now().setZone(tz).toISODate());
  const [reportMode, setReportMode] = useState("Daily"); // Daily | Monthly | Custom
  const [monthYear, setMonthYear] = useState(now.toFormat("yyyy-MM")); // yyyy-MM for <input type="month" />
  const [rangeFrom, setRangeFrom] = useState(now.minus({ days: 6 }).toISODate());
  const [rangeTo, setRangeTo] = useState(now.toISODate());
  const [dailyBookings, setDailyBookings] = useState(null);
  const [dailyRevenue, setDailyRevenue] = useState(null);
  const [monthlyAppointmentStatus, setMonthlyAppointmentStatus] = useState({
    scheduled: 0,
    confirmed: 0,
    completed: 0,
  });
  const [monthlySlotStatus, setMonthlySlotStatus] = useState({
    available: 0,
    booked: 0,
    blocked: 0,
  });
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [billRows, setBillRows] = useState([]);
  const [patientSearch, setPatientSearch] = useState("");
  const [debouncedPatientSearch, setDebouncedPatientSearch] = useState("");
  const [doctorNameFilter, setDoctorNameFilter] = useState("");
  const [invoiceFilter, setInvoiceFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [expandedDateKeys, setExpandedDateKeys] = useState({});
  const [patientWiseSummary, setPatientWiseSummary] = useState({
    totalRevenue: 0,
    billsCount: 0,
  });
  const [loading, setLoading] = useState(false);

  // Debounce patient search so API is not called on every keystroke.
  useEffect(() => {
    const raw = String(patientSearch || "");
    const delay = raw.trim() ? 450 : 0;
    const t = setTimeout(() => {
      setDebouncedPatientSearch(raw.trim());
    }, delay);
    return () => clearTimeout(t);
  }, [patientSearch]);

  const extractRevenueTrendRows = (trendData) => {
    // Backend may return: { items: [...] } OR { rows: [...] } OR raw array
    const raw =
      (trendData && Array.isArray(trendData.items) && trendData.items) ||
      (trendData && Array.isArray(trendData.rows) && trendData.rows) ||
      (Array.isArray(trendData) && trendData) ||
      [];

    // Normalize date field for UI (backend often uses `_id` for the day)
    return raw.map((r) => ({
      ...r,
      date: r?.date || r?._id,
    }));
  };

  const fetchPatientWiseRevenue = useCallback(async (common, range) => {
    const params = {
      ...common,
      page,
      limit,
    };
    if (reportMode === "Daily") {
      params.date = range.from;
    } else {
      params.from = range.from;
      params.to = range.to;
    }

    if (String(debouncedPatientSearch || "").trim()) {
      params.search = String(debouncedPatientSearch).trim();
    }
    if (String(doctorNameFilter || "").trim()) params.doctorName = String(doctorNameFilter).trim();

    try {
      const res = await axiosInstance.get("/reports/revenue-patient-wise", { params });
      const data = res && res.data ? res.data : res;
      return {
        rows: normalizeBillRows(data),
        total: Number(data?.total || data?.pagination?.total || 0),
        summary: {
          totalRevenue: Number(data?.summary?.totalRevenue || 0),
          billsCount: Number(data?.summary?.billsCount || 0),
        },
      };
    } catch (err) {
      // Graceful fallback when endpoint is not available in local/older backend.
      if (err?.response?.status === 404) {
        try {
          const fallbackParams = reportMode === "Daily"
            ? { ...common, date: range.from, page, limit }
            : { ...common, from: range.from, to: range.to, page, limit };
          if (String(debouncedPatientSearch || "").trim()) {
            fallbackParams.search = String(debouncedPatientSearch).trim();
          }
          if (String(doctorNameFilter || "").trim()) {
            fallbackParams.doctorName = String(doctorNameFilter).trim();
          }
          const fb = await axiosInstance.get("/bills", { params: fallbackParams });
          const fbData = fb && fb.data ? fb.data : fb;
          const rows = normalizeBillRows(fbData);
          return {
            rows,
            total: Number(fbData?.total || rows.length || 0),
            summary: {
              totalRevenue: rows.reduce((sum, r) => sum + Number(r.totalAmount || 0), 0),
              billsCount: rows.length,
            },
          };
        } catch {
          return { rows: [], total: 0, summary: { totalRevenue: 0, billsCount: 0 } };
        }
      }
      return { rows: [], total: 0, summary: { totalRevenue: 0, billsCount: 0 } };
    }
  }, [debouncedPatientSearch, doctorNameFilter, limit, page, reportMode]);

  useEffect(() => {
    async function loadReports() {
      if (!selectedBranchId) {
        setDailyBookings(null);
        setDailyRevenue(null);
        setRevenueTrend([]);
        setBillRows([]);
        return;
      }
      setLoading(true);
      try {
        const common = { branchId: selectedBranchId };
        if (String(doctorNameFilter || "").trim()) {
          common.doctorName = String(doctorNameFilter).trim();
        }
        const range = buildDateRange(reportMode, reportDate, monthYear, rangeFrom, rangeTo, tz);

        // Custom range mode: aggregate stats across each day in the range.
        if (reportMode === "Custom") {
          const startDT = DateTime.fromISO(range.from).setZone(tz);
          const endDT = DateTime.fromISO(range.to).setZone(tz);
          const days = [];
          let cursor = startDT;
          // Cap at 90 days to avoid excessive parallel requests.
          while (cursor <= endDT && days.length < 90) {
            days.push(cursor.toISODate());
            cursor = cursor.plus({ days: 1 });
          }

          const [trendRes, ...bookingResults] = await Promise.allSettled([
            axiosInstance.get("/reports/revenue-trend", {
              params: { ...common, from: range.from, to: range.to },
            }),
            ...days.map((d) =>
              axiosInstance.get("/reports/daily-bookings", { params: { ...common, date: d } })
            ),
          ]);

          const rangeAppt = { scheduled: 0, confirmed: 0, completed: 0 };
          const rangeSlot = { available: 0, booked: 0, blocked: 0 };
          for (const r of bookingResults) {
            if (r.status !== "fulfilled") continue;
            const data = r.value && r.value.data ? r.value.data : r.value;
            const appt = data?.appointmentStatus || {};
            const slot = data?.slotStatus || {};
            rangeAppt.scheduled += Number(appt.scheduled || 0);
            rangeAppt.confirmed += Number(appt.confirmed || 0);
            rangeAppt.completed += Number(appt.completed || 0);
            rangeSlot.available += Number(slot.available || 0);
            rangeSlot.booked += Number(slot.booked || 0);
            rangeSlot.blocked += Number(slot.blocked || 0);
          }

          const trendData = trendRes.status === "fulfilled"
            ? (trendRes.value?.data ?? trendRes.value)
            : [];
          const patientWise = await fetchPatientWiseRevenue(common, range);
          setDailyBookings(null);
          setDailyRevenue(null);
          setMonthlyAppointmentStatus(rangeAppt);
          setMonthlySlotStatus(rangeSlot);
          setRevenueTrend(extractRevenueTrendRows(trendData));
          setBillRows(patientWise.rows);
          setTotalRows(patientWise.total);
          setPatientWiseSummary(patientWise.summary);
          return;
        }

        // Monthly mode: show revenue trend for selected month only.
        if (reportMode === "Monthly") {
          const { from: monthStart, to: monthEnd } = getMonthFromTo(monthYear, tz);

          const trendRes = await axiosInstance.get("/reports/revenue-trend", {
            params: { ...common, from: monthStart, to: monthEnd },
          });

          // Aggregate appointment + slot statuses over each day in the month.
          const startDT = DateTime.fromISO(monthStart).setZone(tz);
          const endDT = DateTime.fromISO(monthEnd).setZone(tz);
          const days = [];
          let cursor = startDT;
          while (cursor <= endDT) {
            days.push(cursor.toISODate());
            cursor = cursor.plus({ days: 1 });
          }

          const bookingPromises = days.map((d) =>
            axiosInstance.get("/reports/daily-bookings", { params: { ...common, date: d } })
          );

          const bookingRes = await Promise.allSettled(bookingPromises);

          const monthlyAppt = { scheduled: 0, confirmed: 0, completed: 0 };
          const monthlySlot = { available: 0, booked: 0, blocked: 0 };

          for (const r of bookingRes) {
            if (r.status !== "fulfilled") continue;
            const data = r.value && r.value.data ? r.value.data : r.value;
            const appt = data?.appointmentStatus || {};
            const slot = data?.slotStatus || {};
            monthlyAppt.scheduled += Number(appt.scheduled || 0);
            monthlyAppt.confirmed += Number(appt.confirmed || 0);
            monthlyAppt.completed += Number(appt.completed || 0);
            monthlySlot.available += Number(slot.available || 0);
            monthlySlot.booked += Number(slot.booked || 0);
            monthlySlot.blocked += Number(slot.blocked || 0);
          }

          const trendData = trendRes && trendRes.data ? trendRes.data : trendRes;
          const patientWise = await fetchPatientWiseRevenue(common, range);
          setDailyBookings(null);
          setDailyRevenue(null);
          setMonthlyAppointmentStatus(monthlyAppt);
          setMonthlySlotStatus(monthlySlot);
          setRevenueTrend(extractRevenueTrendRows(trendData));
          setBillRows(patientWise.rows);
          setTotalRows(patientWise.total);
          setPatientWiseSummary(patientWise.summary);
        } else {
          // Daily mode: keep existing daily cards + show trend for current month to today.
          const currentNow = DateTime.now().setZone(tz);
          const trendFrom = currentNow.startOf("month").toISODate();
          const trendTo = currentNow.toISODate();

          const [bookRes, revRes, trendRes] = await Promise.all([
            axiosInstance.get("/reports/daily-bookings", { params: { ...common, date: reportDate } }),
            axiosInstance.get("/reports/daily-revenue", { params: { ...common, date: reportDate } }),
            axiosInstance.get("/reports/revenue-trend", { params: { ...common, from: trendFrom, to: trendTo } }),
          ]);
          const bookData = bookRes && bookRes.data ? bookRes.data : bookRes;
          const revData = revRes && revRes.data ? revRes.data : revRes;
          const trendData = trendRes && trendRes.data ? trendRes.data : trendRes;
          const patientWise = await fetchPatientWiseRevenue(common, range);
          setDailyBookings(bookData || null);
          setDailyRevenue(revData || null);
          setRevenueTrend(extractRevenueTrendRows(trendData));
          setBillRows(patientWise.rows);
          setTotalRows(patientWise.total);
          setPatientWiseSummary(patientWise.summary);
          setMonthlyAppointmentStatus({ scheduled: 0, confirmed: 0, completed: 0 });
          setMonthlySlotStatus({ available: 0, booked: 0, blocked: 0 });
        }
      } catch (err) {
        setDailyBookings(null);
        setDailyRevenue(null);
        setRevenueTrend([]);
        setBillRows([]);
        setTotalRows(0);
        setPatientWiseSummary({ totalRevenue: 0, billsCount: 0 });
        setMonthlyAppointmentStatus({ scheduled: 0, confirmed: 0, completed: 0 });
        setMonthlySlotStatus({ available: 0, booked: 0, blocked: 0 });
      } finally {
        setLoading(false);
      }
    }
    loadReports();
  }, [selectedBranchId, reportDate, reportMode, monthYear, rangeFrom, rangeTo, tz, doctorNameFilter, page, limit, fetchPatientWiseRevenue]);

  const monthlyTotals = revenueTrend.reduce(
    (acc, row) => {
      acc.totalRevenue += Number(row?.totalRevenue || 0);
      acc.paidBills += Number(row?.billsCount || 0);
      return acc;
    },
    { totalRevenue: 0, paidBills: 0 }
  );

  const filteredBillRows = billRows.filter((row) => {
    if (invoiceFilter === "downloaded" && !row.invoiceDownloaded) return false;
    if (invoiceFilter === "not_downloaded" && row.invoiceDownloaded) return false;
    return true;
  });
  const groupedBillRows = (() => {
    const map = new Map();
    filteredBillRows.forEach((row) => {
      const dt = row.date ? DateTime.fromISO(String(row.date)) : null;
      const key = dt && dt.isValid ? dt.toISODate() : "unknown";
      if (!map.has(key)) {
        map.set(key, {
          dateKey: key,
          dateLabel: dt && dt.isValid ? dt.toFormat("dd-LL-yyyy") : "-",
          billCount: 0,
          totalAmount: 0,
          rows: [],
        });
      }
      const group = map.get(key);
      group.billCount += 1;
      group.totalAmount += Number(row.totalAmount || 0);
      group.rows.push(row);
    });
    return Array.from(map.values()).sort((a, b) => {
      if (a.dateKey === "unknown") return 1;
      if (b.dateKey === "unknown") return -1;
      return new Date(b.dateKey).getTime() - new Date(a.dateKey).getTime();
    });
  })();
  const totalPages = Math.max(1, Math.ceil(Number(totalRows || 0) / Number(limit || 20)));

  return (
    <div className="reports-page">
      <Header branches={branches} selectedBranchId={selectedBranchId} onBranchChange={onBranchChange} />
      <div className="reports-wrap">
        <div className="reports-head">
          <h2>Revenue Reports</h2>
          <div className="reports-filters reports-top-filters">
            <select value={reportMode} onChange={(e) => {
              setReportMode(e.target.value);
              setPage(1);
            }}>
              <option value="Daily">Daily</option>
              <option value="Monthly">Monthly</option>
              <option value="Custom">Custom Range</option>
            </select>
            {reportMode === "Daily" && (
              <input type="date" value={reportDate} onChange={(e) => {
                setReportDate(e.target.value);
                setPage(1);
              }} />
            )}
            {reportMode === "Monthly" && (
              <input type="month" value={monthYear} onChange={(e) => {
                setMonthYear(e.target.value);
                setPage(1);
              }} />
            )}
            {reportMode === "Custom" && (
              <div className="reports-range-inputs">
                <label className="reports-range-label">From</label>
                <input
                  type="date"
                  value={rangeFrom}
                  max={rangeTo || undefined}
                  onChange={(e) => {
                    setRangeFrom(e.target.value);
                    setPage(1);
                  }}
                />
                <label className="reports-range-label">To</label>
                <input
                  type="date"
                  value={rangeTo}
                  min={rangeFrom || undefined}
                  max={now.toISODate()}
                  onChange={(e) => {
                    setRangeTo(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <>
            <div className="report-cards">
                  {reportMode === "Daily" && (
                    <>
                      <div className="report-card">
                        <div className="label">Daily Revenue</div>
                        <div className="value">₹{Number(dailyRevenue?.totalRevenue || patientWiseSummary.totalRevenue || 0).toLocaleString()}</div>
                        <div className="sub">Paid Bills: {dailyRevenue?.paidBills || patientWiseSummary.billsCount || 0}</div>
                      </div>
                      <div className="report-card">
                        <div className="label">Appointment Status</div>
                        <div className="sub">Scheduled: {dailyBookings?.appointmentStatus?.scheduled || 0}</div>
                        <div className="sub">Confirmed: {dailyBookings?.appointmentStatus?.confirmed || 0}</div>
                        <div className="sub">Completed: {dailyBookings?.appointmentStatus?.completed || 0}</div>
                      </div>
                      <div className="report-card">
                        <div className="label">Slot Status</div>
                        <div className="sub">Available: {dailyBookings?.slotStatus?.available || 0}</div>
                        <div className="sub">Booked: {dailyBookings?.slotStatus?.booked || 0}</div>
                        <div className="sub">Blocked: {dailyBookings?.slotStatus?.blocked || 0}</div>
                      </div>
                    </>
                  )}
                  {reportMode === "Monthly" && (
                    <>
                      <div className="report-card">
                        <div className="label">Monthly Revenue</div>
                        <div className="value">₹{Number(monthlyTotals.totalRevenue || 0).toLocaleString()}</div>
                        <div className="sub">Paid Bills: {Number(monthlyTotals.paidBills || 0).toLocaleString()}</div>
                      </div>
                      <div className="report-card">
                        <div className="label">Appointment Status</div>
                        <div className="sub">Scheduled: {monthlyAppointmentStatus?.scheduled || 0}</div>
                        <div className="sub">Confirmed: {monthlyAppointmentStatus?.confirmed || 0}</div>
                        <div className="sub">Completed: {monthlyAppointmentStatus?.completed || 0}</div>
                      </div>
                      <div className="report-card">
                        <div className="label">Slot Status</div>
                        <div className="sub">Available: {monthlySlotStatus?.available || 0}</div>
                        <div className="sub">Booked: {monthlySlotStatus?.booked || 0}</div>
                        <div className="sub">Blocked: {monthlySlotStatus?.blocked || 0}</div>
                      </div>
                    </>
                  )}
                  {reportMode === "Custom" && (
                    <>
                      <div className="report-card">
                        <div className="label">Revenue ({rangeFrom} → {rangeTo})</div>
                        <div className="value">₹{Number(patientWiseSummary.totalRevenue || 0).toLocaleString()}</div>
                        <div className="sub">Paid Bills: {patientWiseSummary.billsCount || 0}</div>
                      </div>
                      <div className="report-card">
                        <div className="label">Appointment Status</div>
                        <div className="sub">Scheduled: {monthlyAppointmentStatus?.scheduled || 0}</div>
                        <div className="sub">Confirmed: {monthlyAppointmentStatus?.confirmed || 0}</div>
                        <div className="sub">Completed: {monthlyAppointmentStatus?.completed || 0}</div>
                      </div>
                      <div className="report-card">
                        <div className="label">Slot Status</div>
                        <div className="sub">Available: {monthlySlotStatus?.available || 0}</div>
                        <div className="sub">Booked: {monthlySlotStatus?.booked || 0}</div>
                        <div className="sub">Blocked: {monthlySlotStatus?.blocked || 0}</div>
                      </div>
                    </>
                  )}
            </div>

            <div className="reports-filters reports-table-filters">
              <input
                type="text"
                placeholder="Search patient name/phone"
                value={patientSearch}
                onChange={(e) => {
                  setPatientSearch(e.target.value);
                  setPage(1);
                }}
              />
              <input
                type="text"
                placeholder="Doctor name (optional)"
                value={doctorNameFilter}
                onChange={(e) => {
                  setDoctorNameFilter(e.target.value);
                  setPage(1);
                }}
              />
              <select value={invoiceFilter} onChange={(e) => setInvoiceFilter(e.target.value)}>
                <option value="all">All invoices</option>
                <option value="downloaded">Downloaded</option>
                <option value="not_downloaded">Not downloaded</option>
              </select>
              <select
                value={String(limit)}
                onChange={(e) => {
                  setLimit(Number(e.target.value) || 20);
                  setPage(1);
                }}
              >
                <option value="20">20 rows</option>
                <option value="50">50 rows</option>
                <option value="100">100 rows</option>
                <option value="200">200 rows</option>
              </select>
            </div>

            {loading && <div className="reports-table-loading">Updating table...</div>}

            <div className={`reports-accordion${loading ? " is-loading" : ""}`}>
              {groupedBillRows.length > 0 ? (
                groupedBillRows.map((group) => {
                  const isOpen = !!expandedDateKeys[group.dateKey];
                  return (
                    <div key={group.dateKey} className="reports-accordion-item">
                      <button
                        type="button"
                        className="reports-accordion-head"
                        onClick={() =>
                          setExpandedDateKeys((prev) => ({
                            ...prev,
                            [group.dateKey]: !prev[group.dateKey],
                          }))
                        }
                      >
                        <span>{group.dateLabel}</span>
                        <span>Bills: {group.billCount}</span>
                        <strong>₹{Number(group.totalAmount || 0).toLocaleString()}</strong>
                        <span className="reports-accordion-caret">{isOpen ? "▾" : "▸"}</span>
                      </button>
                      {isOpen && (
                        <div className="reports-table-wrap">
                          <table className="reports-table">
                            <thead>
                              <tr>
                                <th>Bill No</th>
                                <th>Patient</th>
                                <th>Phone</th>
                                <th>Status</th>
                                <th>Amount</th>
                                <th>Invoice Status</th>
                                <th>Invoice</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.rows.map((row) => (
                                <tr key={row.id}>
                                  <td>{row.billNumber || "-"}</td>
                                  <td>{row.patientName || "-"}</td>
                                  <td>{row.patientPhone || "-"}</td>
                                  <td>{String(row.status || "-").toUpperCase()}</td>
                                  <td>₹{Number(row.totalAmount || 0).toLocaleString()}</td>
                                  <td>{row.invoiceDownloaded ? "Downloaded" : "Not downloaded"}</td>
                                  <td>
                                    {row.invoiceDownloadUrl ? (
                                      <a href={row.invoiceDownloadUrl} target="_blank" rel="noreferrer">
                                        Download
                                      </a>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="reports-empty-cell">No billing rows found for selected filters.</div>
              )}
            </div>
            <div className="reports-pagination">
              <div className="reports-pagination-info">
                Page {Math.min(page, totalPages)} of {totalPages} ({totalRows} rows)
              </div>
              <div className="reports-pagination-actions">
                <button
                  type="button"
                  className="page-btn"
                  disabled={loading || page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="page-btn"
                  disabled={loading || page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>

            
          </>
      </div>
    </div>
  );
}
